import { Outlet, useLocation } from "react-router-dom";
import { UnreadBadge } from "@/components/notifications/UnreadBadge";
import { Suspense, useState } from "react";
import { PageLoader } from "@/components/ui/PageLoader";
import { useScrollResetOnRouteChange } from "@/hooks/useScrollResetOnRouteChange";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Menu, ArrowLeft } from "lucide-react";
import { SidebarProvider, useSidebar } from "@/components/ui/sidebar";
import { useCurrentPageTitle } from "@/contexts/PageTitleContext";
import { useNotifications } from "@/hooks/useNotifications";
import { NotificationToast } from "@/components/notifications/NotificationToast";
import { PdfViewerProvider } from "@/contexts/PdfViewerContext";
import { PdfViewerOverlay } from "@/components/ui/PdfViewerOverlay";
import { AssistantProvider } from "@/contexts/AssistantContext";
import { AssistantButton } from "@/components/assistant/AssistantButton";
import { PageTransition } from "@/components/shell/PageTransition";
import { PeritoTopBar } from "@/components/perito/PeritoTopBar";
import { PeritoDock } from "@/components/perito/PeritoDock";
import { PeritoSidebar } from "@/components/perito/PeritoSidebar";
import { PeritoProfileSheet } from "@/components/perito/PeritoProfileSheet";
import { PeritoCompanySwitcher } from "@/components/perito/PeritoCompanySwitcher";
import { cn } from "@/lib/utils";

/**
 * Cabecera fija de tablet/escritorio del perito — la misma que `StickyPageHeader`
 * de `MainLayout`: banda con el degradado `--band`, esquinas de 22, la misma
 * sombra y márgenes, los mismos globos decorativos, hamburguesa en escritorio
 * (pliega la barra lateral), avatar en tablet y el título centrado en Poppins.
 */
function PeritoStickyHeader() {
  const { title, actions, leftActions, hideDesktopTopBar, onMobileBack, desktopBackOnly } = useCurrentPageTitle();
  const { toggleSidebar } = useSidebar();
  const { t } = useTranslation();
  const { profile, user, linkedCompanies, switchCompany } = useAuth();
  const { unreadCount } = useNotifications();
  const [profileOpen, setProfileOpen] = useState(false);

  const displayTitle = title || t("perito.portal");
  if (hideDesktopTopBar) return null;

  const fullName = profile?.full_name || user?.user_metadata?.full_name || user?.email || "P";
  const initial = (fullName || "P").charAt(0).toUpperCase();
  const hasUnread = (unreadCount ?? 0) > 0;

  return (
    <>
      <header
        className="hidden md:flex shrink-0 z-40 relative items-center px-[18px] gap-3 rounded-[22px] shadow-[0_12px_30px_rgba(20,60,100,0.18)] ml-4 mr-3.5 mt-3 min-h-[62px] overflow-hidden"
        style={{ background: "var(--band)" }}
      >
        <div aria-hidden className="pointer-events-none absolute -top-16 -right-12 w-48 h-48 rounded-full bg-white/12" />
        <div aria-hidden className="pointer-events-none absolute -bottom-20 right-32 w-40 h-40 rounded-full bg-white/8" />

        <div className="flex items-center gap-2 min-w-0 relative z-10">
          {!desktopBackOnly && !onMobileBack && (
            <button
              onClick={toggleSidebar}
              className="hidden lg:flex w-[38px] h-[38px] rounded-[11px] bg-white/[0.18] border border-white/30 items-center justify-center hover:bg-white/25 transition-colors flex-shrink-0"
              aria-label="Toggle menu"
            >
              <Menu className="w-5 h-5 text-white" />
            </button>
          )}
          {!desktopBackOnly && !onMobileBack && (
            <button
              onClick={() => setProfileOpen(true)}
              className="flex lg:hidden relative w-[38px] h-[38px] rounded-full bg-white/25 border border-white/30 items-center justify-center text-white font-bold text-sm hover:bg-white/30 transition-colors flex-shrink-0"
              aria-label={t("nav.profile", "Profile")}
            >
              {initial}
              {hasUnread && <UnreadBadge count={unreadCount} />}
            </button>
          )}
          {onMobileBack && (
            <button
              onClick={onMobileBack}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-white text-sm font-semibold hover:bg-white/30 active:scale-95 transition-all flex-shrink-0"
              aria-label={t("common.back")}
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{t("common.back")}</span>
            </button>
          )}
          {leftActions && <div className="flex items-center gap-2 flex-shrink-0">{leftActions}</div>}
          {/* En la tableta la barra lateral no se ve: el conmutador va aquí (tanda 5,
              A9). En el ordenador ya está en la barra lateral. */}
          {linkedCompanies.length > 1 && (
            <div className="lg:hidden min-w-0 max-w-[180px]">
              <PeritoCompanySwitcher
                companies={linkedCompanies}
                currentCompanyId={profile?.company_id || null}
                onSwitch={switchCompany}
                variant="inline"
              />
            </div>
          )}
        </div>

        <h1 className="hidden md:flex absolute inset-0 items-center justify-center font-['Poppins'] font-bold text-xl tracking-[-0.01em] text-white pointer-events-none z-10">
          {displayTitle}
        </h1>

        {actions && (
          <div className="flex items-center gap-2 flex-shrink-0 ml-auto md:mr-6 relative z-10">{actions}</div>
        )}
      </header>
      <PeritoProfileSheet open={profileOpen} onOpenChange={setProfileOpen} />
    </>
  );
}

export function PeritoLayout() {
  const location = useLocation();
  const { latestNotification, clearLatestNotification } = useNotifications();

  useScrollResetOnRouteChange(() => [document.getElementById("perito-scroll-container")]);

  return (
    <AssistantProvider>
      <PdfViewerProvider>
        {/* Mismo armazón que `MainLayout`: la barra lateral vive dentro de un
            SidebarProvider con el mismo ancho de iconos, así hereda el plegado,
            el atajo de teclado y la hoja lateral del móvil. Antes esto era un
            <nav> fijo de 56 sin plegado ni degradado. */}
        <SidebarProvider className="ulture-sidebar-provider" style={{ "--sidebar-width-icon": "5rem" } as React.CSSProperties}>
          <div
            className="h-[100dvh] flex w-full max-w-[100vw] overflow-hidden"
            style={{ background: "var(--bg-page)" }}
          >
            <PeritoSidebar />

            <main className="flex-1 min-h-0 flex flex-col overflow-hidden">
              <PeritoTopBar />
              <PeritoStickyHeader />

              <div
                id="perito-scroll-container"
                className={cn(
                  "relative flex-1 min-h-0 max-w-full flex flex-col",
                  "dock-safe-scroll overflow-y-auto overflow-x-auto lg:after:hidden",
                )}
              >
                <PageTransition>
                  <Suspense fallback={<PageLoader variant="list" />}>
                    <Outlet />
                  </Suspense>
                </PageTransition>
              </div>

              <PeritoDock />
            </main>
          </div>

          <AssistantButton />
          <PdfViewerOverlay />
        </SidebarProvider>

        <NotificationToast notification={latestNotification} onDismiss={clearLatestNotification} />
      </PdfViewerProvider>
    </AssistantProvider>
  );
}
